// src/crm/hooks/useApplications.ts
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '../../lib/api'
import type { Application, Creator, Deal } from '../../lib/types'

const applicationKeys = {
  all: ['applications'] as const,
  list: (appId: string) => ['applications', 'list', appId] as const,
}

export function useApplications(appId: string) {
  return useQuery<Application[], Error>({
    queryKey: applicationKeys.list(appId),
    queryFn: () =>
      api
        .get<{ applications: Application[] }>(`/api/applications?appId=${encodeURIComponent(appId)}`)
        .then((r) => r.applications),
    enabled: Boolean(appId),
    staleTime: 1000 * 60 * 2,
  })
}

type AcceptResult = {
  application: Application
  creator: Creator
  deal: Deal
}

export function useAcceptApplication() {
  const queryClient = useQueryClient()
  return useMutation<AcceptResult, Error, string>({
    mutationFn: (id) =>
      api.post<AcceptResult>(`/api/applications/${id}/accept`, {}),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all })
    },
  })
}

export function usePatchApplicationStatus() {
  const queryClient = useQueryClient()
  return useMutation<
    { application: Application },
    Error,
    { id: string; status: Application['status'] }
  >({
    mutationFn: ({ id, status }) =>
      api.patch<{ application: Application }>(`/api/applications/${id}`, { status }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: applicationKeys.all })
    },
  })
}
